import { useState } from 'react';
import { ChevronDown, MessageCircle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { isEnglishPath } from '../utils/i18nRoutes';

const faqItemsDe = [
  {
    question: 'Was ist KI-Beratung und was bringt sie meinem Unternehmen?',
    answer:
      'KI-Beratung umfasst die Analyse Ihrer Geschäftsprozesse, die Identifikation von Einsatzmöglichkeiten für KI-Technologie und die technische Umsetzung. Das Ziel ist, manuelle Aufgaben zu automatisieren und Betriebskosten zu senken. Bei OptiAI kombinieren wir das mit strategischer Kostenoptimierung in Energie, Telekommunikation und Beschaffung.',
  },
  {
    question: 'Für welche Unternehmensgrößen ist eine KI-Beratung sinnvoll?',
    answer:
      'KI-Beratung lohnt sich ab etwa 50 Mitarbeitern, besonders im Bereich von 100 bis 500 Beschäftigten. Unternehmen dieser Größe haben ausreichend wiederkehrende Prozesse und Betriebsausgaben, dass sich die Automatisierung und Kostenoptimierung rechnet. In einem kurzen Gespräch klären wir, ob das für Ihr Unternehmen zutrifft.',
  },
  {
    question: 'Wie funktioniert die erfolgsbasierte Vergütung bei OptiAI?',
    answer:
      'Wir arbeiten nicht nach Tagessätzen, sondern orientieren uns am Erfolg. Die Vergütung richtet sich nach den nachweislich erzielten Einsparungen oder Effizienzgewinnen. In der Analyse- und Strategiephase entstehen keine Kosten für Sie. Erst wenn Maßnahmen umgesetzt sind und Ergebnisse liefern, beteiligen wir uns am Erfolg.',
  },
  {
    question: 'In welchen Regionen bietet OptiAI KI-Beratung an?',
    answer:
      'Unser Sitz ist in Wien. Wir betreuen Unternehmen in ganz Österreich sowie in Deutschland und der Schweiz. Die meisten Aufgaben erledigen wir remote; für wichtige Termine kommen wir zu Ihnen. Details zur KI-Beratung in Wien finden Sie auf der Seite KI-Beratung Wien.',
  },
  {
    question: 'Wie lange dauert die Implementierung einer KI-Lösung?',
    answer:
      'Einfache Anwendungen wie einen KI-Telefonassistenten setzen wir in vier bis sechs Wochen um. Komplexere Prozessautomatisierungen dauern drei bis sechs Monate. Wir starten mit einer ersten Lösung, die schnell Ergebnisse liefert, und erweitern dann schrittweise.',
  },
];

const faqItemsEn = [
  {
    question: 'What is AI consulting and what does it bring to my company?',
    answer:
      'AI consulting includes analyzing your business processes, identifying use cases for AI technology, and implementing them technically. The goal is to automate manual tasks and reduce operating costs. At OptiAI, we combine this with strategic cost optimization in energy, telecommunications, and procurement.',
  },
  {
    question: 'For which company sizes is AI consulting worthwhile?',
    answer:
      'AI consulting pays off from about 50 employees, especially in the range of 100 to 500 employees. Companies of this size have enough recurring processes and operating expenses that automation and cost optimization are worthwhile. In a brief conversation, we clarify whether this applies to your company.',
  },
  {
    question: 'How does the success-based compensation work at OptiAI?',
    answer:
      'We do not work with daily rates but orient ourselves to success. Our compensation is based on the demonstrably achieved savings or efficiency gains. No costs arise for you during the analysis and strategy phase. Only when measures are implemented and deliver results do we share in the success.',
  },
  {
    question: 'In which regions does OptiAI offer AI consulting?',
    answer:
      'Our headquarters is in Vienna. We serve companies throughout Austria as well as in Germany and Switzerland. We handle most tasks remotely; for important meetings, we come to you. Details about AI consulting in Vienna can be found on the AI Consulting Vienna page.',
  },
  {
    question: 'How long does the implementation of an AI solution take?',
    answer:
      'We implement simple applications such as an AI phone assistant in four to six weeks. More complex process automations take three to six months. We start with an initial solution that delivers results quickly, then expand step by step.',
  },
];

function buildFaqJsonLd(items: typeof faqItemsDe) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

export const faqJsonLd = buildFaqJsonLd(faqItemsDe);
export const faqJsonLdEn = buildFaqJsonLd(faqItemsEn);

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const location = useLocation();
  const isEn = isEnglishPath(location.pathname);
  const faqItems = isEn ? faqItemsEn : faqItemsDe;

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-20 px-6 bg-soft dark:bg-gray-900">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <span className="eyebrow">FAQ</span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-dark dark:text-white mt-2 mb-4">
            {isEn ? 'Frequently Asked Questions' : 'Häufig gestellte Fragen'}
          </h2>
          <p className="text-lg text-muted dark:text-gray-300 max-w-2xl mx-auto leading-relaxed">
            {isEn
              ? 'Answers to the most common questions about AI consulting, cost optimization, and working with OptiAI.'
              : 'Antworten auf die häufigsten Fragen zur KI-Beratung, zur Kostenoptimierung und zur Zusammenarbeit mit OptiAI.'}
          </p>
        </div>

        <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
          {faqItems.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={index}
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                className={`bg-white dark:bg-gray-800 rounded-card border transition-all duration-200 ${
                  isOpen
                    ? 'border-primary dark:border-accent shadow-card'
                    : 'border-line dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left"
                  aria-expanded={isOpen}
                >
                  <h3
                    itemProp="name"
                    className={`text-base font-semibold pr-6 leading-snug transition-colors ${
                      isOpen ? 'text-primary dark:text-accent-light' : 'text-dark dark:text-white'
                    }`}
                  >
                    {item.question}
                  </h3>
                  <span
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                      isOpen
                        ? 'bg-primary text-white rotate-180'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    <ChevronDown className="w-4 h-4 transition-transform" />
                  </span>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    isOpen ? 'max-h-[500px]' : 'max-h-0'
                  }`}
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <p
                    itemProp="text"
                    className="px-6 pb-5 text-muted dark:text-gray-300 leading-relaxed text-sm border-t border-line dark:border-gray-700 pt-4"
                  >
                    {item.answer}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 text-center bg-white dark:bg-gray-800 rounded-card p-8 border border-line dark:border-gray-700 shadow-card">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-tint text-primary mb-4">
            <MessageCircle className="w-6 h-6" />
          </div>
          <h3 className="text-xl font-bold text-dark dark:text-white mb-2">
            {isEn ? 'Still have questions?' : 'Noch Fragen?'}
          </h3>
          <p className="text-muted dark:text-gray-400 mb-6 max-w-md mx-auto text-sm leading-relaxed">
            {isEn
              ? 'Our experts answer your individual questions in a free initial consultation.'
              : 'Unsere Experten beantworten Ihre individuellen Fragen in einem kostenlosen Erstgespräch.'}
          </p>
          <a href="#anfrage" className="btn-primary text-sm">
            {isEn ? 'Request free consultation' : 'Kostenlose Beratung anfragen'}
          </a>
        </div>
      </div>
    </section>
  );
}
